import { COGNITO_CLIENT_ID, COGNITO_REGION } from '@volee/config'

import type { AuthConfig } from './types.ts'

/**
 * Hosted UI domain prefix of the staging pool.
 *
 * The pool is `volee-staging`, created by `infra/modules/cognito`, which also chooses
 * this prefix — so for the first time the two read the same. (The old hand-made pool
 * was named volee-demo while its domain was the flattened pool id, and the mismatch
 * cost a 404.) Overridable with `VITE_COGNITO_HOSTED_UI_DOMAIN`. Not in
 * `@volee/config` because a prefix is not derivable from a pool id: it is chosen when
 * the domain is created.
 */
const DEFAULT_HOSTED_UI_PREFIX = 'volee-staging'

/** `openid` is required for an id_token; `email` and `profile` feed the identity claims. */
export const DEFAULT_SCOPES: readonly string[] = ['openid', 'email', 'profile']

function hostedUiOrigin(): string {
  const explicit = import.meta.env?.VITE_COGNITO_HOSTED_UI_URL
  if (explicit) return explicit.replace(/\/$/, '')
  const prefix = import.meta.env?.VITE_COGNITO_HOSTED_UI_DOMAIN || DEFAULT_HOSTED_UI_PREFIX
  return `https://${prefix}.auth.${COGNITO_REGION}.amazoncognito.com`
}

/**
 * Config for the current environment, with the redirect URIs derived from where the
 * app is actually served.
 *
 * `origin` is a parameter rather than a read of `window.location` so the native shell
 * can pass its universal link, and so tests do not depend on the ambient location.
 */
export function createAuthConfig(origin: string, overrides: Partial<AuthConfig> = {}): AuthConfig {
  const base = origin.replace(/\/$/, '')
  return {
    hostedUiOrigin: hostedUiOrigin(),
    clientId: COGNITO_CLIENT_ID,
    redirectUri: `${base}/auth/callback`,
    postLogoutRedirectUri: base,
    scopes: DEFAULT_SCOPES,
    ...overrides,
  }
}
